/**
 * Grading-scheme extraction. Like the master-solution step, one implementation for the first
 * extraction and every re-upload. Beyond the master solution it also produces the exam-level
 * grammar-grading rules and reports point differences against the confirmed structure.
 */

import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { Exam } from '../../exam.js';
import {
  clearSolutionTasks,
  setSolutionPointAdjustments,
  setSolutionStatus,
  startSolutionProgress,
  StepState,
} from '../../extraction-progress.js';
import { normalizeAdditionalInfoForPrompt } from '../agents/additional-info-context.js';
import { bbxKind } from '../agents/bbx-kind.js';
import { runBoundingBoxAgent } from '../agents/extraction-modules/bounding-box.js';
import {
  buildExamTasksFromRecursiveStructure,
  buildLanguageUseSchemeRules,
  type LanguageUseRuleMergeTarget,
} from '../agents/extraction-modules/build-exam-tasks.js';
import {
  collectExtractedPointDiffs,
  enrichTasksWithOCRByBbx,
  parseAgentAnswer,
} from '../agents/extraction-modules/task-enrichment.js';
import {
  countExtractedFieldEntries,
  countSolutionUnitNodes,
  createCostTracker,
  loadStepInput,
  persistExtractedArtifact,
  type StepResult,
} from './step-utils.js';

const CONTEXT = 'GS-Extraktion';

export interface GradingSchemeStepResult extends StepResult {
  /**
   * Leaf paths where the uploaded grading scheme stated different points than the confirmed
   * structure. Announced rather than applied silently, so the teacher reviews them in the canvas.
   */
  pointDiffs: string[];
}

export async function runGradingSchemeStep(examId: string): Promise<GradingSchemeStepResult> {
  const input = await loadStepInput(examId);
  if (!input) return { ok: false, skipped: false, pointDiffs: [] };

  const gradingSchemeIds: string[] = Array.isArray(input.exam.gradingSchemeIds) ? input.exam.gradingSchemeIds : [];
  if (gradingSchemeIds.length === 0) {
    logger.info({ examId, context: CONTEXT }, 'Kein Bewertungsschema hochgeladen — Schritt übersprungen');
    return { ok: true, skipped: true, pointDiffs: [] };
  }
  if (input.taskRecords.length === 0) {
    logger.error({ examId, context: CONTEXT }, 'Keine Aufgabenstruktur vorhanden — Extraktion nicht möglich');
    return { ok: false, skipped: false, pointDiffs: [] };
  }

  const examCollection = await getCollection(Exam as any);
  const cost = createCostTracker(examId);
  let failed = false;
  let pointDiffs: string[] = [];

  // See the master-solution step: only a stale upload error is cleared; `gradingSchemeIds` is the record
  // that a grading scheme was uploaded.
  await examCollection.updateOne({ _id: examId } as any, {
    $unset: { 'examState.gradingSchemeError': '' },
  });

  try {
    logger.info({ examId, imageCount: gradingSchemeIds.length, context: CONTEXT }, 'Starte Extraktion');
    // See the master-solution step: a re-extraction re-reads every task, so the previous run's finished
    // units are dropped before this one starts counting.
    await clearSolutionTasks(examId, 'gradingScheme');
    await startSolutionProgress(examId, 'gradingScheme', {
      producer: 'extraction',
      total: countSolutionUnitNodes(input.taskRecords),
      message: 'progress.extractingGradingScheme',
    });
    // Some criteria only state a per-item rule (e.g. "1 point per labeled organ") whose total depends on
    // counting something shown in a diagram — the GS document text alone can't resolve that, so the exam's
    // additional-info images are attached too, tagged with the id a task's `additionalInfoIds` points at.
    const additionalInfoItems = normalizeAdditionalInfoForPrompt((input.exam as any)?.additionalInformation);
    const referenceImages = additionalInfoItems.flatMap(item => item.imageIds.map(imageId => ({ id: item.id, imageId })));

    const bbxResult = await runBoundingBoxAgent(gradingSchemeIds, bbxKind.GS, input.minimalTasksJson, referenceImages);
    cost.addCost(bbxResult?.cost, 'gradingSchemeBoundingBox');
    if (!bbxResult) throw new Error('Grading scheme BBX detection returned no results');

    const rawAnswer = typeof bbxResult === 'string' ? bbxResult : bbxResult?.answer;
    if (!rawAnswer) throw new Error('No regions found in grading scheme BBX result');

    const parsed = parseAgentAnswer(bbxResult);
    // Compare the document's points against the confirmed structure BEFORE the merge below.
    pointDiffs = collectExtractedPointDiffs(input.taskRecords, parsed);
    // Also written to the record, because the caller that announces them is no longer the caller that gets
    // this return value: under the job system the run is closed by whichever transition is last, which has
    // only the exam to read. The return value stays for the legacy in-process pipeline (`run.ts`).
    await setSolutionPointAdjustments(examId, 'gradingScheme', pointDiffs);

    // Exam-level grammar-grading rules from the GS (non-task content). Always $set — an empty array
    // clears stale rules on re-upload. Non-fatal: a failure here must not fail the whole step.
    let contentFallbackByTaskNumber: Map<string, LanguageUseRuleMergeTarget> | undefined;
    try {
      // NO `mergeByTaskNumber` ANY MORE. It carried criteria to be merged into a task's own `grammarCriteria`,
      // which is a field that no longer exists: every grammar rule is a LanguageUseScheme now, solo or multi-member,
      // so there is nothing left to merge INTO a task. `buildLanguageUseSchemeRules` returns only the schemes plus the
      // content fallback for tasks whose OCR text is not a rule.
      const { languageUseSchemes, contentFallbackByTaskNumber: fallback } = await buildLanguageUseSchemeRules(
        parsed,
        gradingSchemeIds,
        input.taskRecords,
        c => cost.addCost(c, 'gradingSchemeLanguageUseOcr'),
        examId,
        'gradingScheme',
      );
      contentFallbackByTaskNumber = fallback;
      await examCollection.updateOne({ _id: examId } as any, {
        $set: { languageUseSchemeRules: languageUseSchemes.map(rule => rule.toObject()) as any },
      });
      logger.info(
        { examId, grammarRuleCount: languageUseSchemes.length, context: CONTEXT },
        'Grammatik-Regeln gespeichert',
      );
    } catch (err) {
      logger.error({ err, examId, context: CONTEXT }, 'Fehler beim Extrahieren der Grammatik-Regeln');
    }

    logger.info({ examId, context: CONTEXT }, 'Starte OCR-Anreicherung');
    const enrichedTasks = await enrichTasksWithOCRByBbx(
      input.taskRecords,
      parsed,
      gradingSchemeIds,
      'gradingCriteria',
      true,
      c => cost.addCost(c, 'gradingSchemeCriteriaOcr'),
      examId,
      'gradingScheme',
    );

    const extractedCount = countExtractedFieldEntries(enrichedTasks || input.taskRecords, 'gradingCriteria');
    if (extractedCount <= 0) {
      logger.error({ examId, context: CONTEXT }, 'Keine verwertbaren Bewertungskriterien extrahiert');
      await setSolutionStatus(examId, 'gradingScheme', StepState.ERROR, 'progress.noUsableGradingScheme');
      await cost.flush();
      return { ok: false, skipped: false, pointDiffs };
    }
    logger.info({ examId, extractedCount, context: CONTEXT }, 'OCR-Anreicherung abgeschlossen');

    try {
      const examTasks = await buildExamTasksFromRecursiveStructure(enrichedTasks || input.taskRecords, examId, undefined, {
        contentFallbackByTaskNumber,
      });

      // Only the `gradingScheme` and `declaredMaxPoints` fields, so a master-solution extraction can run
      // alongside this one. A grading scheme is now in place, so the stale FIXED parent totals are dropped as
      // part of this write — see `persistExtractedArtifact`.
      await persistExtractedArtifact(examId, examTasks, 'gradingScheme');
      logger.info({ examId, taskCount: examTasks.length, context: CONTEXT }, 'Bewertungskriterien gespeichert');
    } catch (err) {
      logger.error({ err, examId, context: CONTEXT }, 'Fehler beim Erstellen/Mergen von Tasks');
      await setSolutionStatus(examId, 'gradingScheme', StepState.ERROR, 'progress.errorStructureGradingScheme');
      await cost.flush();
      return { ok: false, skipped: false, pointDiffs };
    }
  } catch (err) {
    logger.error({ err, examId, context: CONTEXT }, 'KRITISCHER FEHLER bei Bewertungsschema-Extraktion');
    failed = true;
  }

  await setSolutionStatus(
    examId,
    'gradingScheme',
    failed ? StepState.ERROR : StepState.COMPLETED,
    failed ? 'progress.errorExtractingGradingScheme' : 'progress.gradingSchemeExtracted',
  );
  await cost.flush();

  return { ok: !failed, skipped: false, pointDiffs };
}
